import styled from '@emotion/styled';
import { Tooltip } from 'antd';
import React from 'react';
import { connect } from 'react-redux';

import completedIcon from 'src/public/img/icon-completed.svg';
import lockIconBlue from 'src/public/img/icon-lock-blue.svg';
import lockIconGray from 'src/public/img/icon-lock-gray.svg';

import { AppState } from 'src/config/appState';
import { WebExclusiveBadge } from 'src/modules/programs/components/WebExclusiveBadge';
import { Button } from 'src/shared/components/Button';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

import { ProgramEnum, ProgramWeekEnum } from '../../models/programs';
import {
    getProgramWeekCardIndex,
    getProgramWeekCardUnlockText,
} from '../../selectors/programsCardsSelectors';
import {
    isSingleProgramSingleWeekCompleted,
    isSingleProgramSingleWeekLocked,
} from '../../selectors/programsSelectors';

interface CorePillarsItemProps {
    wide?: boolean;
}

const CorePillarsItemWrapper = styled('div')<CorePillarsItemProps>(({ wide }) => ({
    position: 'relative',
    flex: wide ? '0 0 auto' : '0 0 215px',
    minWidth: '15em',
    margin: '0 0.5% 35px',
    [mediaQueries.md]: {
        flexDirection: 'column',
    },
}));

interface CorePillarsItemHolderProps extends CorePillarsItemProps {
    isClickable?: boolean;
}

const CorePillarsItemHolder = styled('div')<CorePillarsItemHolderProps>(
    ({ wide, isClickable }) => ({
        display: 'flex',
        position: 'relative',
        flexDirection: wide ? 'row' : 'column',
        borderRadius: '0.25rem',
        border: `2px solid ${color.catskillWhite}`,
        alignItems: 'flex-start',
        padding: wide ? '2rem 1.5rem' : '30px 10px 10px',
        margin: `0 0 ${wide ? '1rem' : 0}`,
        cursor: !wide && isClickable ? 'pointer' : 'auto',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
            borderColor: !wide && isClickable ? color.royalBlueSaturated : color.gallery,
        },
        [mediaQueries.md]: {
            flexDirection: 'column',
        },
    }),
);

const IconCompleted = styled('i')({
    display: 'inline-block',
    backgroundSize: 'cover',
    position: 'absolute',
    backgroundImage: `url(${completedIcon})`,
    width: 12,
    height: 12,
    right: 12,
    top: 12,
});

const ItemIconWrapper = styled('div')<CorePillarsItemProps>(({ wide }) => ({
    position: 'relative',
    minWidth: wide ? '9rem' : '175px',
    minHeight: wide ? '9rem' : '175px',
    margin: `0 ${wide ? '1.5rem 0 0' : 'auto 2rem'}`,
    transition: 'all 0.2s ease-in-out',
    [mediaQueries.md]: {
        marginBottom: wide ? '1.5rem' : '2rem',
    },
}));

interface ItemIconProps {
    iconUrl: string;
}

const ItemIcon = styled('i')<ItemIconProps>(({ iconUrl }) => ({
    position: 'absolute',
    height: '100%',
    width: '175px',
    backgroundImage: `url('${iconUrl}')`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
}));

const ItemWeek = styled('h5')<CorePillarsItemProps>(({ wide }) =>
    wide
        ? {
              ...font.heavy(font.size.large),
              color: color.tundora,
              marginBottom: '0.5rem',
          }
        : {
              ...font.book(15),
              color: color.ebonyClay,
              marginBottom: 0,
          },
);

interface ItemNameProps {
    inline?: boolean;
}

const ItemName = styled('span')<ItemNameProps>(
    ({ inline }) =>
        inline
            ? null
            : {
                  ...font.heavy(font.size.mediumExtended),
                  color: color.pickledBluewood,
                  marginTop: 0,
                  marginBottom: 8,
                  display: 'block',
              },
    {
        wordBreak: 'break-all',
        textTransform: 'capitalize',
    },
);

const ItemDescription = styled('p')(
    {
        color: color.scorpion,
        lineHeight: 1.5,
        marginBottom: '0.5rem',
    },
    font.book(font.size.base),
);

interface IconLockProps {
    buttonmode?: number;
}

export const IconLock = styled('i')<IconLockProps>(({ buttonmode }) => ({
    display: 'inline-block',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    position: 'absolute',
    backgroundImage: `url(${buttonmode ? lockIconGray : lockIconBlue})`,
    width: 14,
    height: 14,
    left: buttonmode ? 9 : 'auto',
    right: buttonmode ? 'auto' : 12,
    top: buttonmode ? 9 : 12,
}));

interface StateProps {
    formattedWeekIndex: string | undefined;
    isLocked: boolean;
    isCompleted: boolean;
    unlockText: string | undefined;
}

interface Props extends StateProps {
    weekId: ProgramWeekEnum;
    title: string;
    iconUrl: string;
    startWeekText: string;
    onClick: () => void;
    wide?: boolean;
    description?: string;
    webExclusive?: boolean;
}

const CorePillarsItemContainer: React.SFC<Props> = ({
    formattedWeekIndex,
    isCompleted,
    isLocked,
    title,
    iconUrl,
    startWeekText,
    onClick,
    wide,
    description,
    unlockText,
    webExclusive,
}) => {
    return (
        <CorePillarsItemWrapper wide={wide}>
            <Tooltip placement="bottom" title={isLocked && !wide && unlockText}>
                <CorePillarsItemHolder
                    className="category-item"
                    wide={wide}
                    isClickable={!isLocked}
                    onClick={() => !isLocked && !wide && onClick()}
                >
                    {webExclusive && <WebExclusiveBadge />}
                    {!wide && isLocked && <IconLock />}
                    {isCompleted && <IconCompleted />}
                    <ItemIconWrapper wide={wide}>
                        <ItemIcon iconUrl={iconUrl} />
                    </ItemIconWrapper>
                    <div>
                        <ItemWeek wide={wide}>
                            <ItemName inline={wide}> {title}</ItemName>
                            {formattedWeekIndex}
                        </ItemWeek>
                        {wide && description && <ItemDescription>{description}</ItemDescription>}
                        {wide && (
                            <Button small type="accept" disabled={isLocked} onClick={onClick}>
                                <>
                                    {!isLocked || <IconLock buttonmode={1} />}
                                    {!isLocked ? startWeekText : unlockText}
                                </>
                            </Button>
                        )}
                    </div>
                </CorePillarsItemHolder>
            </Tooltip>
        </CorePillarsItemWrapper>
    );
};

const mapStateToProps = (state: AppState, { weekId }: Props): StateProps => ({
    formattedWeekIndex: getProgramWeekCardIndex(state, ProgramEnum.CP, weekId),
    isLocked: isSingleProgramSingleWeekLocked(state, ProgramEnum.CP, weekId),
    isCompleted: isSingleProgramSingleWeekCompleted(state, ProgramEnum.CP, weekId),
    unlockText: getProgramWeekCardUnlockText(state, ProgramEnum.CP, weekId),
});

export const CorePillarsItem = connect<StateProps>(mapStateToProps)(CorePillarsItemContainer);
